import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { UsersClient } from '../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../api/apiConfig';

/**
 * Permission Context Type
 */
interface PermissionContextType {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  userId: string | null;
  email: string | null;
  isLoading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

/**
 * Hook to access permission context
 * @throws Error if used outside of PermissionProvider
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Permission Provider Component
 * 
 * Manages user permissions extracted from Clerk JWT token.
 * Automatically loads permissions when user signs in.
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { isSignedIn, getToken, isLoaded } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // Set up authenticated fetch with Clerk token provider
  useEffect(() => {
    authenticatedFetch.setTokenProvider(getToken);
  }, [getToken]);

  /**
   * Load permissions from backend API
   * Note: The backend enriches JWT claims server-side, but frontend needs explicit API call
   * since the token claims added during OnTokenValidated are not included in the Clerk token
   */
  const refreshPermissions = useCallback(async () => {
    // Wait for Clerk to fully load
    if (!isLoaded) {
      return;
    }

    const requestId = ++requestIdRef.current;

    if (!isSignedIn) {
      // User not signed in, clear everything
      setPermissions([]);
      setUserId(null);
      setEmail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Fetch permissions from backend API
      const client = new UsersClient(getApiBaseUrl(), authenticatedFetch);
      const response = await client.getApiUsersMePermissions();
      
      // Set user data and permissions from API response
      if (requestId === requestIdRef.current) {
        setUserId(response.userId || null);
        setEmail(response.email || null);
        setPermissions(response.permissions || []);
      }

    } catch (loadError) {
      console.error('Failed to load permissions:', loadError);
      if (requestId === requestIdRef.current) {
        setError('permissions.errors.load');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [isLoaded, isSignedIn]);

  // Load permissions when Clerk loads or user signs in/out
  useEffect(() => {
    void refreshPermissions();
  }, [refreshPermissions]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (...requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(p => permissions.includes(p));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (...requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(p => permissions.includes(p));
  };

  const value: PermissionContextType = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userId,
    email,
    isLoading,
    error,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Permission Constants
 * 
 * Centralized list of all available permissions in the system.
 * Use these constants instead of string literals for type safety.
 */
export const Permissions = {
  USERS_ACCESS: 'users:access',
  USER_MANAGE: 'user:manage',
  ROLE_MANAGE: 'role:manage',
  ADMIN_READ_AUDIT: 'admin:read:audit',
  PROJECT_READ: 'project:read',
  PROJECT_MANAGE: 'project:manage',
  PROJECT_MERGE: 'project:merge',
  DOCUMENT_UPLOAD: 'document:upload',
  DOCUMENT_REVIEW: 'document:review',
  DOCUMENT_REFILE: 'document:refile',
  CONFLICT_RESOLVE: 'conflict:resolve',
  ANALYSIS_RUN: 'analysis:run',
  QUERY_EXECUTE: 'query:execute',
  PUBLICATION_MANAGE: 'publication:manage',
  OPS_RECOVER: 'ops:recover',
  CLIENT_PORTAL: 'client:portal',
  EMAIL_RELEASE: 'email:release',
  SETTINGS_MANAGE: 'settings:manage',
} as const;

export type Permission = typeof Permissions[keyof typeof Permissions];
